(() => {
  function $q(ctx, selector, callback) {
    const el = ctx.querySelector(selector);
    if (el) callback(el, 0, ctx);
  }

  $q.all = function $qAll(ctx, selector, callback) {
    let els = ctx.querySelectorAll(selector);
    if (els.length === 0) return;
    for (let i = 0, l = els.length; i < l; i++) {
      callback(els[i], i, ctx);
    }
  };

  // Cached query selector
  function $cqs(selector, context = document) {
    // Check if the selector is already cached for this context
    // if (!$cqs._cache) {
    //   // If not, create a cache object
    //   $cqs._cache = new WeakMap();
    // }

    // Check if the selector is already in the cache for this context
    if (!$cqs._cache.has(context)) {
      $cqs._cache.set(context, new Map());
    }

    const cache = $cqs._cache.get(context);
    if (!cache.has(selector)) {
      // If not, query the DOM for the context and store the result in the cache
      cache.set(selector, context.querySelector(selector));
    }

    // Return the cached value
    return cache.get(selector);
  }
  $cqs._cache = new WeakMap();

  $cqs.all = function $cqsAll(selector, context = document) {
    if (!$cqs.all._cache.has(context)) {
      $cqs.all._cache.set(context, new Map());
    }

    const cache = $cqs.all._cache.get(context);
    if (!cache.has(selector)) {
      // If not, query the DOM for the context and store the result in the cache
      cache.set(selector, context.querySelectorAll(selector));
    }
    // Return the cached value
    return cache.get(selector);
  };
  $cqs.all._cache = new WeakMap();

  const _HYDRATE_SKIP = Symbol("SKIP");
  const FN_ATTR_EFFECTS_LOOKUP = {
    ":show": (el, result) => {
      const show = !!result;
      el.toggleAttribute("hidden", !show);
      // el.style.display = show ? '' : 'none'
    },
    ":text": (el, result) => {
      el.textContent = result == null ? "" : result;
    },
    ":effect": null,
  };
  const SUPPORTED_KEYS = Object.keys(FN_ATTR_EFFECTS_LOOKUP);

  const _children_cache = new WeakMap();

  const _GLOBAL_FN_CACHE = new Map();
  function _customCompiledFn(cacheKey, params, body) {
    const key = `${cacheKey}::${params.join(",")}::${body}`;

    let fn = _GLOBAL_FN_CACHE.get(key);
    if (!fn) {
      fn = new Function("$el", "$SKIP", "$cqs", ...params, body);
      _GLOBAL_FN_CACHE.set(key, fn);
    }
    return fn;
  }
  function hydrateFromHtml({
    container,
    preferCachedChildren = false,
    // tuple of params and args
    context = [],
  }) {
    const fnAttrsLookup = FN_ATTR_EFFECTS_LOOKUP;
    const data = context.reduce(
      (acc, tuple) => {
        if (!tuple[0] || typeof tuple[0] !== "string" || tuple.length !== 2) {
          throw new Error(
            "hydrateFromHtml: context must be [name, value] tuples",
          );
        }
        acc.params.push(tuple[0]);
        acc.args.push(tuple[1]);
        return acc;
      },
      { params: [], args: [] },
    );

    let els = _children_cache.get(container);
    if (!preferCachedChildren) {
      preferCachedChildren =
        container.getAttribute("static-children") === "true";
    }

    if (!els || !preferCachedChildren) {
      els = [container].concat(
        Array.from(
          container.querySelectorAll(
            `${SUPPORTED_KEYS.map((x) => `[${CSS.escape(x)}]`).join(", ")}`,
          ),
        ),
      );
      _children_cache.set(container, els);
    }

    els.forEach((el) => {
      if (!el) return;
      let elFns = el["__@hsy_compiled_fns"] || (el["__@hsy_compiled_fns"] = {});

      const condFnString = el.getAttribute(":hydration-guard");
      if (condFnString) {
        const condFnCacheKey = `__fn_:hydration-guard`;
        let condFn = elFns[condFnCacheKey];
        if (condFnString && !condFn) {
          condFn = _customCompiledFn(
            ":hydration-guard",
            data.params,
            `return (${condFnString})`,
          );
          elFns[condFnCacheKey] = condFn;
        }

        if (condFn) {
          const shouldContinue = condFn(el, _HYDRATE_SKIP, $cqs, ...data.args);
          if (!shouldContinue) return;
        }
      }

      for (const fnAttr in fnAttrsLookup) {
        if (!Object.prototype.hasOwnProperty.call(fnAttrsLookup, fnAttr)) {
          continue;
        }
        const condAttr = `${fnAttr}::hydration-guard`;

        const ourEffect = fnAttrsLookup[fnAttr];
        const userFnString = el.getAttribute(fnAttr);
        const condFnString = el.getAttribute(condAttr);

        if (!userFnString) {
          continue;
        }

        const fnCacheKey = `__fn_${fnAttr}`;
        const condFnCacheKey = `__fn_${condAttr}`;

        let userFn = elFns[fnCacheKey];

        if (!userFn) {
          // if there is our effect, then we expect the `userFn` to be returnable
          const returnable = !!ourEffect;
          const fnbody = returnable ? `return (${userFnString})` : userFnString;
          userFn = _customCompiledFn(fnAttr, data.params, fnbody);
          elFns[fnCacheKey] = userFn;
        }

        let condFn = elFns[condFnCacheKey];
        if (condFnString && !condFn) {
          condFn = _customCompiledFn(
            `${fnAttr}::hydration-guard`,
            data.params,
            `return (${condFnString})`,
          );
          elFns[condFnCacheKey] = condFn;
        }

        try {
          if (condFn) {
            const shouldContinue = condFn(
              el,
              _HYDRATE_SKIP,
              $cqs,
              ...data.args,
            );
            if (!shouldContinue) continue;
          }

          // Run user fn
          const result = userFn(el, _HYDRATE_SKIP, $cqs, ...data.args);
          if (result === _HYDRATE_SKIP) continue;

          // Run effect - if any
          if (ourEffect) {
            ourEffect(el, result);
          }
        } catch (e) {
          if (!el.hasAttribute("x-no-err")) {
            console.error("[hydrateFromHtml] :: ", e.message);
          }
        }
      }
    });
  }

  hydrateFromHtml._customCompiledFn = _customCompiledFn;
  hydrateFromHtml._HYDRATE_SKIP = _HYDRATE_SKIP;
  hydrateFromHtml._$cqs = $cqs;
  hydrateFromHtml._$q = $q;

  window.hydrateFromHtml = hydrateFromHtml;
  window.__hydrateFromHtml = hydrateFromHtml;
})();

// ----------------------------------------------------------------------------------------------------------

(() => {
  const event_source_cache = new WeakMap();

  /**
   * @example
   * <x-event-reactor event="variant-change" data-source="product-form" static-children="true">
   * </x-event-reactor>
   *
   * <x-event-reactor event="variant-change" data-source="product-form" static-children="true">
   *   <button :listener :onclick="alert('clicked on ' + $target.nodeName)" >Click me</button>
   * </x-event-reactor>
   */
  class XEventReactor extends XThemeBaseElement {
    mount() {
      this.setAttribute("x-dehydrated", "true");

      this._has_user_defined_events = false;
      this._relay_events_setuped = false;

      this._setupSourceRelayListeners();
      this._setupUserDefinedEventListeners();

      if (!this._has_user_defined_events && !this._relay_events_setuped) {
        this.setAttribute(
          "data-error",
          "No events defined / source element/events found",
        );
        return;
      }

      this.removeAttribute("x-dehydrated");
    }

    _setupSourceRelayListeners() {
      const sourceAttr =
        this.getAttribute("source") || this.getAttribute("data-source");
      if (!sourceAttr) return;

      let sourceEl = null;
      try {
        if (sourceAttr === "document") {
          sourceEl = document;
        } else if (sourceAttr === "window") {
          sourceEl = window;
        } else if (sourceAttr === "body") {
          sourceEl = document.body;
        } else if (sourceAttr[0] == "#") {
          sourceEl = document.getElementById(sourceAttr.slice(1));
        } else {
          sourceEl = document.querySelector(sourceAttr);
        }
      } catch (e) {
        console.error(
          `[x-event-reactor] Error finding source element. Invalid selector: "${sourceAttr}"`,
        );
        console.error(e);
        return;
      }

      if (!sourceEl) return;

      this._listenFrom = sourceEl;

      const eventsAttr =
        this.getAttribute("event") || this.getAttribute("data-event");

      this._events = eventsAttr?.split(",").reduce((acc, raw) => {
        const [name, mappedTo = name] = raw.split("->");
        if (!name) return acc;

        acc.push({ name, mappedTo: mappedTo.replace(/\:/g, "_") });
        return acc;
      }, []);

      if (!Array.isArray(this._events) || this._events.length === 0) return;

      this._stateContext = this._events.map((ev, index) => [ev.mappedTo, null]);

      let cache = event_source_cache.get(sourceEl);
      if (!cache) {
        cache = {};
        event_source_cache.set(sourceEl, cache);
      }

      for (let i = 0, l = this._events.length; i < l; i++) {
        const ev = this._events[i];
        this._listenFrom.addEventListener(
          ev.name,
          (domEvent) => {
            if (!this.isConnected) return;

            this._stateContext[i][1] = {
              detail: domEvent.detail,
              target: domEvent.target,
              currentTarget: domEvent.currentTarget,
            };

            __hydrateFromHtml({
              container: this,
              context: [
                ["$event", domEvent],
                ["$detail", domEvent.detail],
                ["$target", domEvent.target],
                ["$currentTarget", domEvent.currentTarget],
                ...this._stateContext,
              ],
            });
            cache[ev.name] = this._stateContext[i][1];
          },
          { signal: this._ac.signal },
        );
      }

      // initial run of context if any
      for (let i = 0, l = this._events.length; i < l; i++) {
        const ev = this._events[i];
        const ctx = cache[ev.name];
        if (ctx) {
          __hydrateFromHtml({
            container: this,
            context: [
              ["$detail", ctx.detail],
              ["$target", ctx.target],
              ["$currentTarget", ctx.currentTarget],
              ...this._stateContext,
            ],
          });
        }
      }

      this._relay_events_setuped = true;
    }

    _setupUserDefinedEventListeners() {
      // 1. Attach to the component itself
      if (this.hasAttribute(":listener")) {
        this._attachUserDefinedEventListeners(this);
      }

      // 2. Attach to all children that have :listener attributes
      const listenerChildren = this.querySelectorAll("[\\:listener]");
      for (let i = 0, l = listenerChildren.length; i < l; i++) {
        const child = listenerChildren[i];
        this._attachUserDefinedEventListeners(child);
      }
    }

    _attachUserDefinedEventListeners(el) {
      const attrs = el.attributes;
      for (let i = 0, l = attrs.length; i < l; i++) {
        const attr = attrs[i];
        if (!attr.name.startsWith(":on")) continue;

        // Extract event name: e.g., :onclick -> click, :oninput -> input
        const [eventName, ...modifiers] = attr.name.slice(3).split("|");
        const handlerCode = attr.value;

        // supported modifiers: once, passive, capture
        const mods =
          modifiers.length > 0
            ? modifiers.reduce((acc, mod) => {
                if (!mod) return acc;
                acc[mod] = true;
                return acc;
              }, {})
            : null;

        // Compile the string into a function with the standard context
        // Parameters match the signature used in hydrateFromHtml
        const handlerFn = __hydrateFromHtml._customCompiledFn(
          `event:${eventName}`,
          ["$event", "$detail", "$target", "$currentTarget"],
          handlerCode,
        );

        this._has_user_defined_events = true;
        el.addEventListener(
          eventName,
          (domEvent) => {
            // Execute with the current state context
            handlerFn(
              // internal context
              el,
              __hydrateFromHtml._HYDRATE_SKIP,
              __hydrateFromHtml.$cqs,

              // our context
              domEvent,
              domEvent.detail,
              domEvent.target,
              domEvent.currentTarget,
            );
          },
          {
            signal: this._ac.signal,
            once: mods?.once,
            passive: mods?.passive,
            capture: mods?.capture,
          },
        );
      }
    }
  }

  if (!window.customElements.get("x-event-reactor")) {
    window.customElements.define("x-event-reactor", XEventReactor);
  }
})();

// ----------------------------------------------------------------------------------------------------------

window.addEventListener(
  "error",
  (event) => {
    const target = event.target;

    // Check if the error came from an <img> tag
    if (target.tagName === "IMG") {
      if (
        target.parentElement?.classList.contains(
          "jm-product-variant-card__thumbnail",
        )
      ) {
        target.style.display = "none";
      }
    }
  },
  true,
);

;(() => {
  const isMobile = window.matchMedia("(max-width: 749.98px)").matches;
  if (isMobile) {
    const hamburger = document.querySelector(
      "#Details-menu-drawer-container .header__icon--summary",
    );
    const menuDrawer = document.getElementById("menu-drawer");
    menuDrawer.style.height = "100vh";
    menuDrawer.style.top = `calc(${hamburger.clientHeight + 1}px)`;
  }
})();

(() => {
  const btn = document.getElementById("ezsearch-global-widget-toggle-btn");
  let modal = document.getElementById("global-fitment-modal");
  if (modal) {
    modal.moveModal();
    modal = document.getElementById("global-fitment-modal");
  }

  setTimeout(() => {
    window._ezs_change_vehicle_fn = function _ezs_change_vehicle_fn(
      openerElement,
    ) {
      // btn.click()
      // requestAnimationFrame(() => btn.focus())
      // btn.scrollIntoView({ behavior: 'smooth' })
      modal.show(openerElement);
    };

    const fitmentForm = modal.querySelector(".EZS_form");
    fitmentForm?.addEventListener("submit", () => {
      modal.hide();
    });
  }, 100);
})();

(() => {
  window.__collection_page_ezsearch_state_machine = {
    selected: {
      collection_page: {
        tag_applied: ({
          els: { p, a },
          selected,
          tag,
          url,
          collection,
          vehicle_title,
        }) => {
          if (p) {
            p.textContent = `You are viewing results for '${vehicle_title}'.`;
          }

          if (a) {
            a.textContent = "View products for all vehicles instead →";
            a.setAttribute("href", `/collections/${collection.handle}`);
          }
        },
        tag_not_applied: ({ els: { p, a }, collection, vehicle_title }) => {
          if (p) {
            p.textContent =
              "You are currently viewing results for all vehicles.";
          }
          if (a) {
            a.textContent = `View products for '${vehicle_title}' only →`;
            a.setAttribute("href", collection.url_filtered);
          }
        },
      },
    },
    not_selected: {
      collection_page: {
        tag_applied: ({ els: { p, a }, collection, url }) => {
          if (collection.handle && url.searchParams.get("filter.p.tag")) {
            url.searchParams.delete("filter.p.tag");
            window.location.href = url.toString();
          }
          if (p) {
            p.textContent = "";
          }
          if (a) {
            a.textContent = "";
          }
        },
        tag_not_applied: ({ els: { p, a } }) => {
          if (p) {
            p.textContent =
              "You are currently viewing results for all vehicles. Select a vehicle to view results for that vehicle only.";
          }
          if (a) {
            a.textContent = "";
          }
        },
      },
    },
  };
})();

(() => {
  function patchCollectionLinksOnPage({ tag, universalTag }) {
    const origin = window.location.origin;
    const collectionLink = document.querySelectorAll(
      'a[href^="/collections/"]',
    );
    for (let i = 0, len = collectionLink.length; i < len; i++) {
      const el = collectionLink[i];
      if (el.__ezs_col_link == false) continue;

      if (el.__ezs_url == null) {
        const href = el.getAttribute("href");
        const url = new URL(href, origin);

        if (url.origin !== origin) {
          el.__ezs_col_link = false;
          continue;
        }

        const skip = el.getAttribute("data-ezs-skip-link") === "true";

        const is_col = skip ? false : isCollectionPathname(url.pathname);
        el.__ezs_col_link = is_col;
        if (!is_col) continue;

        el.__ezs_url = url;
      }
      const url = el.__ezs_url;

      if (tag) {
        url.searchParams.set("filter.p.tag", tag)
        if (universalTag) {
          url.searchParams.append("filter.p.tag", universalTag);
        }
      } else {
        url.searchParams.delete("filter.p.tag");
      }

      el.setAttribute("href", url.pathname + url.search);
    }
  }

  function isCollectionPathname(pathname) {
    const parts = pathname.split("/");
    if (parts[1] === "collections" && !(parts[3] == "products" && parts[4])) {
      return true;
    }
    return false;
  }

  const widget = document.getElementById("ezsearch-global-widget");
  if (widget) {
    window.__ymm_universal_tag = widget.options?.universalTag || ''

    widget.addEventListener("ezsearch::selection_update", ({ detail }) => {
      const universalTag = widget.options?.universalTag || ''
      window.__ymm_universal_tag = universalTag

      const selected = detail.selected
      const tag = selected ? selected._tag : ""
      patchCollectionLinksOnPage({ tag, universalTag })
      refetchCollectionCarousels({ tag, universalTag })
      window.__ymm_applied_tag = tag
    })
  }

  const parser = new DOMParser();
  function refetchCollectionCarousels({ tag, universalTag }) {
    const promisesByCollection = {};

    const featuredCollections = document.querySelectorAll(
      ".section-featured-collection slider-component",
    );
    for (let i = 0, len = featuredCollections.length; i < len; i++) {
      const slider = featuredCollections[i];
      const section = slider.closest(".section-featured-collection");
      if (!section.__default_slider) {
        section.__default_slider = slider;
      }
      const defaultSlider = section.__default_slider;

      if (!tag) {
        if (defaultSlider && slider !== defaultSlider) {
          slider.replaceWith(defaultSlider);
        }
        continue;
      }

      // refetch
      const handle = slider.getAttribute("data-collection-handle");
      if (!handle) continue;
      let qp = `?view=_carousel&filter.p.tag=${tag}`;
      if (universalTag) {
        qp += `&filter.p.tag=${universalTag}`;
      }

      // const url = `/collections/${handle}/${tag}?view=_carousel`;
      const url = `/collections/${handle}${qp}`;
      const promise =
        promisesByCollection[url] ||
        (promisesByCollection[url] = fetch(url)
          .then((res) => res.text())
          .then((str) => parser.parseFromString(str, "text/html").querySelector('slider-component')));
      
      section.setAttribute('data-loading', 'true') 
      promise.then((newSlider) => {
        const count = +slider.getAttribute("data-rendered-products-count");
        if (count > 0) {
          slider.replaceWith(newSlider)
        }
      })
      .finally(() => {
        section.setAttribute('data-loading', 'false')
      })
    }
  }
})();
